import { Office } from "../entities/office";
import type { ISchedule } from "../entities/schedule";
import type {
  ScheduleRequest,
  ScheduleRequestUpdate
} from "../entities/schedule_request";
import { BaseOrganizationEndpoints } from "./base";
import { PaginatedResult } from "./response/paginated_result";

// ============================================================================
// Document Types
// ============================================================================

export type VisitorDocumentType = "nda";

export type VisitorDocument = {
  id: string;
  type: VisitorDocumentType;
  name: string;
  url: string;
  expiration_days: number;
  created: string;
  updated: string;
};

export type VisitorDocumentCreatePayload = {
  type: VisitorDocumentType;
  name: string;
  url: string;
  expiration_days?: number;
};

export type VisitorDocumentUpdatePayload = {
  name?: string;
  url?: string;
  expiration_days?: number;
};

export type GenerateDocumentUploadURLPayload = {
  document_type: VisitorDocumentType;
};

export type VisitorDocumentUploadURLResponse = {
  upload_url: string;
  path: string;
  access_url: string;
};

export type VisitorDocumentsFilter = {
  cursor?: string;
  limit?: number;
};

export type SignedDocument = {
  id: string;
  document: VisitorDocument;
  signed_at: string;
  expires_at?: string;
  signed_document_url: string;
};

export type SignedDocumentsFilter = {
  signed_by: string;
  cursor?: string;
  limit?: number;
};

// ============================================================================
// Visitor Type Types
// ============================================================================

export type VisitorTypeStepType =
  | "visitor_details"
  | "host_selection"
  | "nda_signature"
  | "photo_capture";

export type VisitorTypeFieldType =
  | "name"
  | "text"
  | "email"
  | "phone"
  | "textarea"
  | "checkbox";

export type VisitorTypeStep = {
  id: string;
  step_type: VisitorTypeStepType;
  require_signature: boolean;
  nda_document?: VisitorDocument;
};

export type VisitorTypeField = {
  id: string;
  name: string;
  description?: string;
  field_type: VisitorTypeFieldType;
  is_required: boolean;
};

export type VisitorType = {
  id: string;
  name: string;
  description?: string;
  requires_host: boolean;
  all_users_hosts: boolean;
  steps: VisitorTypeStep[];
  fields: VisitorTypeField[];
  created: string;
  updated: string;
};

export type VisitorTypeStepCreatePayload = {
  step_type: VisitorTypeStepType;
  require_signature: boolean;
  nda_document_id?: string;
};

export type VisitorTypeFieldCreatePayload = {
  name: string;
  description?: string;
  field_type: VisitorTypeFieldType;
  is_required: boolean;
};

export type VisitorTypeCreatePayload = {
  name: string;
  description?: string;
  requires_host: boolean;
  host_user_ids?: string[];
  steps: VisitorTypeStepCreatePayload[];
  fields: VisitorTypeFieldCreatePayload[];
};

export type VisitorTypeStepUpdatePayload = {
  id?: string;
  step_type: VisitorTypeStepType;
  require_signature: boolean;
  nda_document_id?: string;
};

export type VisitorTypeFieldUpdatePayload = {
  id?: string;
  name: string;
  description?: string;
  field_type: VisitorTypeFieldType;
  is_required: boolean;
};

export type VisitorTypeUpdatePayload = {
  name?: string;
  description?: string;
  requires_host?: boolean;
  host_user_ids?: string[];
  steps?: VisitorTypeStepUpdatePayload[];
  fields?: VisitorTypeFieldUpdatePayload[];
};

export type VisitorTypesFilter = {
  cursor?: string;
  limit?: number;
};

export type VisitorTypeHostsFilter = {
  cursor?: string;
  search?: string;
  limit?: number;
};

export type VisitorTypeHost = {
  id: string;
  name: string;
  email: string;
  title?: string;
  phone?: string;
  profile_photo_url?: string;
};

// ============================================================================
// Station Types
// ============================================================================

export type VisitorStationType = "office" | "room" | "wayfinder";

export type StationBranding = {
  logo_url?: string;
  background_image_url?: string;
  welcome_title?: string;
  welcome_subtitle?: string;
  welcome_message?: string;
  background_color?: string;
  primary_color?: string;
  accent_color?: string;
};

/**
 * Resource tag returned by the backend adapter.
 */
export type StationResourceTag = {
  id: string;
  name: string;
  color?: string;
};

/**
 * Resource media variant returned by the backend adapter.
 */
export type StationResourceMedia = {
  id: string;
  url: string;
  type: string;
  name?: string;
};

/**
 * Visible assignment on a resource.
 */
export type StationVisibleAssignment = {
  id: string;
  name: string;
  profile_photo_url: string;
};

/**
 * Resource embedded in station responses for room-type stations.
 * This is the full resource shape returned by the backend adapter.
 */
export type StationResource = {
  id: string;
  office_id: string;
  created_by_user_id: string;
  type: string;
  name: string;
  total_slots: number;
  available_slots: number;
  reserved_slots: number;
  total_pending_schedules: number;
  parent_id?: string;
  is_approvable: boolean;
  is_available: boolean;
  maximum_slots_per_user: number;
  is_open: boolean;
  has_tags: boolean;
  map_url: string;
  map_data?: string;
  weight: number;
  overrides: boolean;
  label: string;
  description?: string;
  main_photo_uri?: string;
  organization_id: string;
  updated_by_user_id: string;
  equipment_cache?: string;
  is_shareable: boolean;
  media_variants?: StationResourceMedia[];
  video_variants?: StationResourceMedia[];
  document_variants?: StationResourceMedia[];
  tags?: StationResourceTag[];
  users?: Record<string, boolean>;
  user_ids?: string[];
  teams?: Record<string, boolean>;
  team_ids?: string[];
  visible_assignment?: StationVisibleAssignment;
  user_visible_assignments?: StationVisibleAssignment[];
  team_visible_assignments?: StationVisibleAssignment[];
  favorite_id?: string;
};

/**
 * Shared station fields (maps to StationResponseBase on the backend).
 * Neither office_id nor office — those are added by the specific response types.
 */
export type VisitorStationResponseBase = {
  id: string;
  created_by_user_id: string;
  name: string;
  type: VisitorStationType;
  floor?: string;
  zone?: string;
  branding?: StationBranding;
  enable_deliveries?: boolean;
  enable_employee_checkin?: boolean;
  enable_auto_checkout?: boolean;
  enable_touchless_checkin?: boolean;
  enable_touchless_badge_print?: boolean;
  idle_timeout_seconds?: number;
  /**
   * Wayfinder-only. When false (the default), the kiosk renders in
   * assignment-view mode — only desks with exactly one permanent user are
   * shown on the map and tapping resolves to that user. When true, the
   * reservation flow takes over (not yet implemented end-to-end).
   */
  allow_reservation?: boolean;
  /** Wayfinder-only: the zone the map is cropped to; whole floor when empty. */
  display_zone_id?: string | null;
  display_zone_name?: string | null;
  /** Wayfinder-only: what the search covers, or "off" to hide it (non-touch displays). */
  search_mode?: WayfinderSearchMode;
  /** Wayfinder-only: shows who's booked in the zone today next to the map. */
  show_people_list?: boolean;
  is_online: boolean;
  visitor_type_ids?: string[];
  resource?: StationResource;
  created: string;
  updated: string;
};

/**
 * Station /me response. Includes a full Office object.
 */
export type VisitorStationBase = VisitorStationResponseBase & {
  office: Office;
  is_remote_login?: boolean;
  /** Wayfinder-only: the display zone's box in map coordinates. */
  display_zone_bounds?: WayfinderZoneBounds | null;
  /** Wayfinder-only: the outline of each of the display zone's spaces, in map coordinates. */
  display_zone_shapes?: WayfinderZonePoint[][];
  /** Wayfinder-only: resources inside the display zone. */
  display_zone_resource_ids?: string[];
};

export type WayfinderSearchMode = "zone" | "floor" | "off";

export type WayfinderZonePoint = { x: number; y: number };

export type WayfinderZoneBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Full station response with admin_code and office_id. Used by admin endpoints (get, list, create, update).
 */
export type VisitorStation = VisitorStationResponseBase & {
  office_id: string;
  admin_code: string;
};

export type VisitorStationCreatePayload = {
  office_id: string;
  name: string;
  type?: VisitorStationType;
  resource_id?: string;
  floor?: string;
  zone?: string;
  branding?: StationBranding;
  enable_deliveries: boolean;
  enable_employee_checkin: boolean;
  enable_auto_checkout?: boolean;
  enable_touchless_checkin?: boolean;
  enable_touchless_badge_print?: boolean;
  idle_timeout_seconds?: number;
  allow_reservation?: boolean;
  display_zone_id?: string | null;
  search_mode?: WayfinderSearchMode;
  show_people_list?: boolean;
  visitor_type_ids?: string[];
};

export type VisitorStationUpdatePayload = {
  name?: string;
  floor?: string;
  zone?: string;
  branding?: StationBranding;
  enable_deliveries?: boolean;
  enable_employee_checkin?: boolean;
  enable_auto_checkout?: boolean;
  enable_touchless_checkin?: boolean;
  enable_touchless_badge_print?: boolean;
  idle_timeout_seconds?: number;
  allow_reservation?: boolean;
  display_zone_id?: string | null;
  search_mode?: WayfinderSearchMode;
  show_people_list?: boolean;
  visitor_type_ids?: string[];
};

export type VisitorStationsFilter = {
  office_id?: string;
  is_online?: boolean;
  type?: VisitorStationType;
  cursor?: string;
  limit?: number;
};

export type LoginStationPayload =
  | { code: string; remote_code?: never }
  | { code?: never; remote_code: string };

export type LoginStationResponse = {
  station_id: string;
  organization_id: string;
  organization_slug: string;
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  id_token: string;
  scope: string;
  expires_in: number;
};

export type GenerateRemoteCodeResponse = {
  remote_code: string;
  expires_at: string;
};

export type HeartbeatStationResponse = {
  station_id: string;
  last_heartbeat: string;
};

export type PairingStationResponse = {
  code: string;
  remaining_seconds: number;
  expires_at: string;
};

export type LogoutStationPayload = {
  admin_code: string;
};

export type ValidateStationAdminCodePayload = {
  admin_code: string;
};

export type ValidateStationAdminCodeResponse = {
  valid: boolean;
};

// ============================================================================
// Visitor Settings Types
// ============================================================================

export type VMNotificationConfig = {
  enabled: boolean;
  logo_url: string;
  brand_color: string;
};

export type VMNotificationSettings = {
  pre_registration_config: VMNotificationConfig;
  visitor_checkin_config: VMNotificationConfig;
  host_config: VMNotificationConfig;
  delivery_config: VMNotificationConfig;
};

export type VisitorSettings = {
  notification_settings: VMNotificationSettings;
  /** Users that can never be picked as a visit host, on any visitor type */
  excluded_host_user_ids: string[];
};

export type VMNotificationConfigUpdatePayload = {
  enabled?: boolean;
  logo_url?: string;
  brand_color?: string;
};

export type VMNotificationSettingsUpdatePayload = {
  pre_registration_config?: VMNotificationConfigUpdatePayload;
  visitor_checkin_config?: VMNotificationConfigUpdatePayload;
  host_config?: VMNotificationConfigUpdatePayload;
  delivery_config?: VMNotificationConfigUpdatePayload;
};

export type VisitorSettingsUpdatePayload = {
  notification_settings?: VMNotificationSettingsUpdatePayload;
  /** Replaces the whole list when sent; omit it to leave the list unchanged */
  excluded_host_user_ids?: string[];
};

// ============================================================================
// Watchlist Types
// ============================================================================

export type WatchlistSeverity = "block" | "flag";

export type WatchlistEntry = {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  severity: WatchlistSeverity;
  notes?: string;
  user_ids: string[];
  created: string;
  updated: string;
};

export type WatchlistEntryCreatePayload = {
  name?: string;
  email?: string;
  phone?: string;
  severity: WatchlistSeverity;
  notes?: string;
  user_ids?: string[];
};

export type WatchlistEntryUpdatePayload = {
  name?: string;
  email?: string;
  phone?: string;
  severity?: WatchlistSeverity;
  notes?: string;
  user_ids?: string[];
};

export type WatchlistFilter = {
  severity?: WatchlistSeverity;
  cursor?: string;
  limit?: number;
};

// ============================================================================
// Visit Types
// ============================================================================

export type VisitStatus = "expected" | "checked_in" | "checked_out";

export type VisitSignature = {
  document_id: string;
  signature_url: string | null;
};

export type VisitFieldValue = {
  field_id: string;
  value: string;
};

export type VisitCreatePayload = {
  office_id: string;
  visitor_type_id?: string;
  host_id?: string;
  visitor_email: string;
  visitor_name: string;
  expected: string;
  visit_fields?: VisitFieldValue[];
};

export type Visitor = {
  id: string;
  name?: string;
  email?: string;
};

export type VisitorListItem = {
  id: string;
  name?: string;
  email?: string;
  last_visit?: Visit | null;
  total_visits: number;
  created: string;
  updated: string;
};

export type VisitorsFilter = {
  search?: string;
  cursor?: string;
  limit?: number;
};

export type VisitorTypeMinimal = {
  id: string;
  name: string;
  description?: string;
  requires_host: boolean;
};

export type VisitHost = {
  id: string;
  name: string;
  email: string;
  title?: string;
  phone?: string;
  profile_photo_url?: string;
};

export type NDASignatureResponse = {
  document_id: string;
  signed_at: string;
  signed_document_url: string;
};

export type Visit = {
  id: string;
  visitor: Visitor;
  visitor_type?: VisitorTypeMinimal;
  office_id?: string;
  station_id?: string;
  host?: VisitHost;
  status: VisitStatus;
  expected?: string;
  checked_in?: string;
  checked_out?: string;
  guest_photo_url?: string;
  nda_signatures: NDASignatureResponse[];
  visit_fields: VisitFieldValue[];
  created: string;
  updated: string;
};

export type VisitRegistration = Visit & {
  code?: string;
};

export type VisitsFilter = {
  office_id?: string;
  station_id?: string;
  visitor_id?: string;
  host_id?: string;
  status?: VisitStatus;
  date_from?: string;
  date_to?: string;
  tz?: string;
  checked_in_from?: string;
  checked_in_to?: string;
  search?: string;
  cursor?: string;
  limit?: number;
};

export type InvitationCodeResponse = {
  code: string;
  url: string;
};

// ============================================================================
// Delivery Types
// ============================================================================

export type DeliveryType = "package" | "food" | "other";

export type DeliveryStatus = "pending" | "delivered";

export type Delivery = {
  id: string;
  office_id: string;
  station_id?: string;
  type: DeliveryType;
  status: DeliveryStatus;
  tracking_number?: string;
  delivery_service?: string;
  recipient_id?: string;
  picked_up_by_id?: string;
  picked_up_at?: string;
  signature_image_path?: string;
  package_image_path?: string;
  vendor?: string;
  description?: string;
  created: string;
  updated: string;
};

export type DeliveryCreatePayload = {
  office_id: string;
  type: DeliveryType;
  tracking_number?: string;
  delivery_service?: string;
  recipient_id?: string;
  vendor?: string;
  description?: string;
  signature_image_path?: string;
  package_image_path?: string;
  station_id?: string;
};

export type DeliveryUpdatePayload = {
  status?: DeliveryStatus;
  picked_up_by_id?: string;
  signature_image_path?: string;
  package_image_path?: string;
  description?: string;
  vendor?: string;
  tracking_number?: string;
  delivery_service?: string;
  recipient_id?: string;
};

export type DeliveriesFilter = {
  office_id?: string;
  type?: DeliveryType;
  status?: DeliveryStatus;
  date_from?: string;
  date_to?: string;
  tz?: string;
  cursor?: string;
  limit?: number;
};

export type StationDeliveryCreatePayload = {
  type: DeliveryType;
  tracking_number?: string;
  delivery_service?: string;
  recipient_id?: string;
  vendor?: string;
  description?: string;
  signature_image_path?: string;
  package_image_path?: string;
};

export type DeliveryUploadType = "delivery_photo" | "delivery_signature";

export type GenerateDeliveryUploadURLPayload = {
  type: DeliveryUploadType;
  format: ImageUploadFormat;
};

export type ScanDeliveryLabelPayload = {
  image_path: string;
};

export type ScanDeliveryLabelResponse = {
  carrier?: string;
  tracking_number?: string;
  recipient_name?: string;
  recipient_address?: string;
  sender_name?: string;
  sender_address?: string;
  weight?: string;
  service_type?: string;
  notes?: string;
};

// ============================================================================
// Notification Types
// ============================================================================

export type NotificationEmailType =
  | "visitor_pre_registration"
  | "visitor_checkin"
  | "host_visitor_checkin"
  | "delivery_received";

export type TestNotificationPayload = {
  email_type: NotificationEmailType;
};

// ============================================================================
// Check-in Types (for kiosk)
// ============================================================================

export type CheckInVisitPayload = {
  invite_code?: string;
  visitor_type_id?: string;
  host_id?: string;
  visitor_email?: string;
  visitor_name?: string;
  guest_photo_url?: string;
  nda_signatures?: VisitSignature[];
  visit_fields?: VisitFieldValue[];
};

export type VisitorSignature = {
  document_id: string;
  signed_at: string;
};

export type VisitorSignaturesFilter = {
  visitor_email: string;
  document_ids: string[];
};

// ============================================================================
// Office with Stations Types
// ============================================================================

export type PublicVisitsSharingResponse = {
  enabled: boolean;
  code: string;
  expires_at?: string;
};

export type OfficeWithStations = {
  office: Office;
  stations: VisitorStation[];
  public_visits_sharing?: PublicVisitsSharingResponse;
};

export type UpdateOfficeSharingPayload = {
  enabled: boolean;
  expires_at: string | null;
};

// ============================================================================
// Invitation Visit Types
// ============================================================================

export type CreateInvitationVisitPayload = {
  office_id: string;
  visitor_type_id: string;
  host_id?: string;
  visitor_email: string;
  visitor_name: string;
  expected: string;
  visit_fields?: VisitFieldValue[];
};

// ============================================================================
// Upload URL Types (for stations and invitations)
// ============================================================================

export type ImageUploadType =
  | "visitor_photo"
  | "visitor_signature"
  | "delivery_photo"
  | "delivery_signature";
export type ImageUploadFormat = "png" | "jpg" | "jpeg";

export type StationImageUploadPayload = {
  format: ImageUploadFormat;
};

export type GenerateImageUploadURLPayload = {
  type: ImageUploadType;
  format: ImageUploadFormat;
};

export type GenerateImageUploadURLResponse = {
  upload_url: string;
  path: string;
  access_url: string;
};

// ============================================================================
// Base Path
// ============================================================================

const BASE_PATH = "/v1/visitor_management";

// ============================================================================
// Client Classes
// ============================================================================

export class VisitorDocumentsClient extends BaseOrganizationEndpoints {
  async list(params?: VisitorDocumentsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorDocument>>(
      "GET",
      `${BASE_PATH}/documents?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<VisitorDocument>(
      "GET",
      `${BASE_PATH}/documents/${id}`
    );
  }

  async create(payload: VisitorDocumentCreatePayload) {
    return this.requestWithStatus<VisitorDocument>(
      "POST",
      `${BASE_PATH}/documents`,
      payload
    );
  }

  async update(id: string, payload: VisitorDocumentUpdatePayload) {
    return this.requestWithStatus<VisitorDocument>(
      "PUT",
      `${BASE_PATH}/documents/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/documents/${id}`
    );
  }

  async generateUploadUrl(payload: GenerateDocumentUploadURLPayload) {
    return this.requestWithStatus<VisitorDocumentUploadURLResponse>(
      "POST",
      `${BASE_PATH}/documents/upload`,
      payload
    );
  }

  async listSigned(params: SignedDocumentsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<SignedDocument>>(
      "GET",
      `${BASE_PATH}/documents/signed?${urlParams.toString()}`
    );
  }
}

export class VisitorTypesClient extends BaseOrganizationEndpoints {
  async list(params?: VisitorTypesFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorType>>(
      "GET",
      `${BASE_PATH}/types?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<VisitorType>(
      "GET",
      `${BASE_PATH}/types/${id}`
    );
  }

  async create(payload: VisitorTypeCreatePayload) {
    return this.requestWithStatus<VisitorType>(
      "POST",
      `${BASE_PATH}/types`,
      payload
    );
  }

  async update(id: string, payload: VisitorTypeUpdatePayload) {
    return this.requestWithStatus<VisitorType>(
      "PUT",
      `${BASE_PATH}/types/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<void>("DELETE", `${BASE_PATH}/types/${id}`);
  }

  async listHosts(id: string, params?: VisitorTypeHostsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorTypeHost>>(
      "GET",
      `${BASE_PATH}/types/${id}/hosts?${urlParams.toString()}`
    );
  }
}

export class VisitorStationsClient extends BaseOrganizationEndpoints {
  async list(params?: VisitorStationsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorStation>>(
      "GET",
      `${BASE_PATH}/stations?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<VisitorStation>(
      "GET",
      `${BASE_PATH}/stations/${id}`
    );
  }

  async create(payload: VisitorStationCreatePayload) {
    return this.requestWithStatus<VisitorStation>(
      "POST",
      `${BASE_PATH}/stations`,
      payload
    );
  }

  async update(id: string, payload: VisitorStationUpdatePayload) {
    return this.requestWithStatus<VisitorStation>(
      "PUT",
      `${BASE_PATH}/stations/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/stations/${id}`
    );
  }

  async login(payload: LoginStationPayload) {
    return this.requestWithStatus<LoginStationResponse>(
      "POST",
      `${BASE_PATH}/stations/login`,
      payload
    );
  }

  async heartbeat() {
    return this.requestWithStatus<HeartbeatStationResponse>(
      "POST",
      `${BASE_PATH}/stations/heartbeat`
    );
  }

  async generatePairingCode(id: string) {
    return this.requestWithStatus<PairingStationResponse>(
      "POST",
      `${BASE_PATH}/stations/${id}/pairing`
    );
  }

  async regenerateAdminCode(id: string) {
    return this.requestWithStatus<VisitorStation>(
      "POST",
      `${BASE_PATH}/stations/${id}/admin-code`
    );
  }

  async listTypes(id: string) {
    return this.requestWithStatus<VisitorType[]>(
      "GET",
      `${BASE_PATH}/stations/${id}/types`
    );
  }

  async listOffices() {
    return this.requestWithStatus<OfficeWithStations[]>(
      "GET",
      `${BASE_PATH}/stations/offices`
    );
  }

  async listOfficeTypes(officeId: string) {
    return this.requestWithStatus<VisitorType[]>(
      "GET",
      `${BASE_PATH}/stations/offices/${officeId}/types`
    );
  }

  async updateOfficeSharing(
    officeId: string,
    payload: UpdateOfficeSharingPayload
  ) {
    return this.requestWithStatus<PublicVisitsSharingResponse>(
      "PUT",
      `${BASE_PATH}/stations/offices/${officeId}/sharing`,
      payload
    );
  }

  async regenerateOfficeSharingCode(officeId: string) {
    return this.requestWithStatus<PublicVisitsSharingResponse>(
      "POST",
      `${BASE_PATH}/stations/offices/${officeId}/sharing/code`
    );
  }

  async generateStationImageUploadUrl(payload: StationImageUploadPayload) {
    return this.requestWithStatus<GenerateImageUploadURLResponse>(
      "POST",
      `${BASE_PATH}/stations/uploads`,
      payload
    );
  }
}

export class WatchlistClient extends BaseOrganizationEndpoints {
  async list(params?: WatchlistFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<WatchlistEntry>>(
      "GET",
      `${BASE_PATH}/watchlists?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<WatchlistEntry>(
      "GET",
      `${BASE_PATH}/watchlists/${id}`
    );
  }

  async create(payload: WatchlistEntryCreatePayload) {
    return this.requestWithStatus<WatchlistEntry>(
      "POST",
      `${BASE_PATH}/watchlists`,
      payload
    );
  }

  async update(id: string, payload: WatchlistEntryUpdatePayload) {
    return this.requestWithStatus<WatchlistEntry>(
      "PUT",
      `${BASE_PATH}/watchlists/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/watchlists/${id}`
    );
  }
}

export class VisitorSettingsClient extends BaseOrganizationEndpoints {
  async get() {
    return this.requestWithStatus<VisitorSettings>(
      "GET",
      `${BASE_PATH}/settings`
    );
  }

  async update(payload: VisitorSettingsUpdatePayload) {
    return this.requestWithStatus<VisitorSettings>(
      "PUT",
      `${BASE_PATH}/settings`,
      payload
    );
  }
}

export class VisitsClient extends BaseOrganizationEndpoints {
  async list(params?: VisitsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<Visit>>(
      "GET",
      `${BASE_PATH}/visits?${urlParams.toString()}`
    );
  }

  async create(payload: VisitCreatePayload) {
    return this.requestWithStatus<VisitRegistration>(
      "POST",
      `${BASE_PATH}/visits`,
      payload
    );
  }

  async get(id: string) {
    return this.requestWithStatus<Visit>("GET", `${BASE_PATH}/visits/${id}`);
  }

  async delete(id: string) {
    return this.requestWithStatus<void>("DELETE", `${BASE_PATH}/visits/${id}`);
  }

  async checkOut(id: string) {
    return this.requestWithStatus<Visit>(
      "POST",
      `${BASE_PATH}/visits/${id}/checkout`
    );
  }

  async createInvitation() {
    return this.requestWithStatus<InvitationCodeResponse>(
      "POST",
      `${BASE_PATH}/visits/invitation`
    );
  }
}

export class VisitorsClient extends BaseOrganizationEndpoints {
  async list(params?: VisitorsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorListItem>>(
      "GET",
      `${BASE_PATH}/visitors?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<VisitorListItem>(
      "GET",
      `${BASE_PATH}/visitors/${id}`
    );
  }
}

// ============================================================================
// Public Sharing Types & Client
// ============================================================================

export type PublicVisit = {
  id: string;
  visitor: Visitor;
  visitor_type?: VisitorTypeMinimal;
  office_id: string;
  station_id: string;
  host?: VisitHost;
  status: VisitStatus;
  expected?: string;
  checked_in?: string;
  checked_out?: string;
  guest_photo_url?: string;
  created: string;
  updated: string;
};

export type PublicSharingVisitsFilter = {
  status?: VisitStatus;
  cursor?: string;
  limit?: number;
};

export class PublicSharingVisitsClient extends BaseOrganizationEndpoints {
  async list(params?: PublicSharingVisitsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<PublicVisit>>(
      "GET",
      `${BASE_PATH}/public/sharing/visits?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<PublicVisit>(
      "GET",
      `${BASE_PATH}/public/sharing/visits/${id}`
    );
  }
}

// ============================================================================
// Wayfinder Types (returned by station-self search & assignment endpoints)
// ============================================================================

export type WayfinderPerson = {
  user_id: string;
  name: string;
  email: string;
  title?: string;
  profile_photo_url?: string;
};

export type WayfinderRoom = {
  resource_id: string;
  name: string;
  description?: string;
  is_available: boolean;
};

export type WayfinderAssignment = {
  resource_id: string;
  resource_name: string;
  office_id: string;
  floor?: string;
  same_office: boolean;
  same_floor: boolean;
};

export type WayfinderAssignmentsResponse = {
  assignments: WayfinderAssignment[];
};

/**
 * Someone sitting in the wayfinder's zone (or floor) today, and where: booked
 * on the desk, or the owner of a dedicated desk nobody booked (no times then).
 */
export type WayfinderZonePerson = {
  user: { id: string; name: string; profile_photo_url?: string | null };
  resource: { id: string; name: string };
  start: string | null;
  end: string | null;
  checked_in: boolean;
  source: "booking" | "dedicated";
};

export type WayfinderSearchParams = {
  q?: string;
  cursor?: string;
  limit?: number;
};

// ============================================================================
// Station Self Client (for kiosk devices)
// ============================================================================

export class StationSelfClient extends BaseOrganizationEndpoints {
  async get() {
    return this.requestWithStatus<VisitorStationBase>(
      "GET",
      `${BASE_PATH}/stations/me`
    );
  }

  async generateRemoteCode() {
    return this.requestWithStatus<GenerateRemoteCodeResponse>(
      "POST",
      `${BASE_PATH}/stations/me/codes`
    );
  }

  /** Visits checked in from a phone that still need their badge printed; each is returned once. */
  async claimBadgePrints() {
    return this.requestWithStatus<Visit[]>(
      "POST",
      `${BASE_PATH}/stations/me/badge-prints/claim`
    );
  }

  async logout(payload: LogoutStationPayload) {
    return this.requestWithStatus<void>(
      "POST",
      `${BASE_PATH}/stations/me/logout`,
      payload
    );
  }

  async validateAdminCode(payload: ValidateStationAdminCodePayload) {
    return this.requestWithStatus<ValidateStationAdminCodeResponse>(
      "POST",
      `${BASE_PATH}/stations/me/admin`,
      payload
    );
  }

  async listTypes() {
    return this.requestWithStatus<VisitorType[]>(
      "GET",
      `${BASE_PATH}/stations/me/types`
    );
  }

  async listHostsForType(typeId: string, params?: VisitorTypeHostsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorTypeHost>>(
      "GET",
      `${BASE_PATH}/stations/me/types/${typeId}/hosts?${urlParams.toString()}`
    );
  }

  async listVisits(params: { search?: string; tz: string }) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<Visit>>(
      "GET",
      `${BASE_PATH}/stations/me/visits?${urlParams.toString()}`
    );
  }

  async getVisitByCode(code: string) {
    return this.requestWithStatus<Visit>(
      "GET",
      `${BASE_PATH}/stations/me/visits/code/${code}`
    );
  }

  /** The visitor's last visit at this station's office; 404 when there is none */
  async getLastVisit(email: string) {
    const urlParams = this.formatGenericParams({ email });
    return this.requestWithStatus<Visit>(
      "GET",
      `${BASE_PATH}/stations/me/visits/last?${urlParams.toString()}`
    );
  }

  async checkIn(payload: CheckInVisitPayload) {
    return this.requestWithStatus<Visit>(
      "POST",
      `${BASE_PATH}/stations/me/visits`,
      payload
    );
  }

  async checkOut(visitId: string) {
    return this.requestWithStatus<Visit>(
      "POST",
      `${BASE_PATH}/stations/me/visits/${visitId}/checkout`
    );
  }

  async getSignatures(params: VisitorSignaturesFilter) {
    const urlParams = new URLSearchParams();
    urlParams.set("visitor_email", params.visitor_email);
    for (const id of params.document_ids) {
      urlParams.append("document_ids", id);
    }
    return this.requestWithStatus<VisitorSignature[]>(
      "GET",
      `${BASE_PATH}/stations/me/signatures?${urlParams.toString()}`
    );
  }

  async generateUploadUrl(payload: GenerateImageUploadURLPayload) {
    return this.requestWithStatus<GenerateImageUploadURLResponse>(
      "POST",
      `${BASE_PATH}/stations/me/uploads`,
      payload
    );
  }

  async createDelivery(payload: StationDeliveryCreatePayload) {
    return this.requestWithStatus<Delivery>(
      "POST",
      `${BASE_PATH}/stations/me/deliveries`,
      payload
    );
  }

  async scanDeliveryLabel(payload: ScanDeliveryLabelPayload) {
    return this.requestWithStatus<ScanDeliveryLabelResponse>(
      "POST",
      `${BASE_PATH}/stations/me/deliveries/scan`,
      payload
    );
  }

  // Schedule endpoints (room display)

  async createSchedule(payload: ScheduleRequest) {
    return this.requestWithStatus<{ id: string; elements: ISchedule[] }>(
      "POST",
      `${BASE_PATH}/stations/me/schedules`,
      payload
    );
  }

  async updateSchedule(scheduleId: string, payload: ScheduleRequestUpdate) {
    return this.requestWithStatus<{ id: string; elements: ISchedule[] }>(
      "PUT",
      `${BASE_PATH}/stations/me/schedules/${encodeURIComponent(scheduleId)}`,
      payload
    );
  }

  async cancelSchedule(scheduleId: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/stations/me/schedules/${encodeURIComponent(scheduleId)}`
    );
  }

  async getOccupied(params: {
    office_id: string;
    date: string;
    tz: string;
    /** Single resource ID — used by the room-display kiosk for its own room. */
    resource_id?: string;
    /** Floor resource ID — fetches all schedules under this floor. */
    floor?: string;
    /** Resource type filter — pair with `floor` to narrow to e.g. meeting_room. */
    type?: string[];
  }) {
    const urlParams = new URLSearchParams();
    urlParams.set("office_id", params.office_id);
    urlParams.set("date", params.date);
    urlParams.set("tz", params.tz);
    if (params.resource_id) {
      urlParams.set("resource_id", params.resource_id);
    }
    if (params.floor) {
      // Backend query param is named `resource` (carried over from earlier
      // schedule routes); keep callers from having to know that.
      urlParams.set("resource", params.floor);
    }
    if (params.type) {
      for (const t of params.type) urlParams.append("type", t);
    }
    return this.requestWithStatus<{ elements: ISchedule[]; cursor?: string }>(
      "GET",
      `${BASE_PATH}/stations/me/schedules/occupied?${urlParams.toString()}`
    );
  }

  async getResourcesSchedulable(params: {
    office_id: string;
    start_time: string;
    end_time: string;
    parent?: string;
    type?: string[];
  }) {
    const urlParams = new URLSearchParams();
    urlParams.set("office_id", params.office_id);
    urlParams.set("start_time", params.start_time);
    urlParams.set("end_time", params.end_time);
    if (params.parent) {
      urlParams.set("parent", params.parent);
    }
    if (params.type) {
      for (const t of params.type) {
        urlParams.append("type", t);
      }
    }
    return this.requestWithStatus<{
      elements: StationResource[];
      non_available?: { resource: StationResource }[];
      tree?: unknown[];
      cursor?: string;
      total?: number;
    }>(
      "GET",
      `${BASE_PATH}/stations/me/resources/schedulable?${urlParams.toString()}`
    );
  }

  // Wayfinder kiosk endpoints

  async searchPeople(params?: WayfinderSearchParams) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<WayfinderPerson>>(
      "GET",
      `${BASE_PATH}/stations/me/search/people?${urlParams.toString()}`
    );
  }

  async searchRooms(params?: WayfinderSearchParams) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<WayfinderRoom>>(
      "GET",
      `${BASE_PATH}/stations/me/search/rooms?${urlParams.toString()}`
    );
  }

  async getUserAssignments(userId: string) {
    return this.requestWithStatus<WayfinderAssignmentsResponse>(
      "GET",
      `${BASE_PATH}/stations/me/assignments/${encodeURIComponent(userId)}`
    );
  }

  async getAssignedResources() {
    return this.requestWithStatus<{
      elements: WayfinderAssignedResource[];
    }>("GET", `${BASE_PATH}/stations/me/resources/assigned`);
  }

  async getPeople() {
    return this.requestWithStatus<{ elements: WayfinderZonePerson[] }>(
      "GET",
      `${BASE_PATH}/stations/me/people`
    );
  }
}

export type WayfinderAssignedResource = {
  resource: StationResource;
  assigned_user: WayfinderPerson;
};

// ============================================================================
// Invitations Client (for pre-registration flow)
// Note: Invitation endpoints use AuthTypeInvitationCode - the invitation code
// should be passed via the Authorization header, not through a login endpoint.
// ============================================================================

export class VisitorInvitationsClient extends BaseOrganizationEndpoints {
  async createVisit(payload: CreateInvitationVisitPayload) {
    return this.requestWithStatus<VisitRegistration>(
      "POST",
      `${BASE_PATH}/public/invitations/visits`,
      payload
    );
  }

  async listOffices() {
    return this.requestWithStatus<OfficeWithStations[]>(
      "GET",
      `${BASE_PATH}/public/invitations/offices`
    );
  }

  async listOfficeTypes(officeId: string) {
    return this.requestWithStatus<VisitorType[]>(
      "GET",
      `${BASE_PATH}/public/invitations/offices/${officeId}/types`
    );
  }

  async generateUploadUrl(payload: GenerateImageUploadURLPayload) {
    return this.requestWithStatus<GenerateImageUploadURLResponse>(
      "POST",
      `${BASE_PATH}/public/invitations/uploads`,
      payload
    );
  }

  async listHostsForType(typeId: string, params?: VisitorTypeHostsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<VisitorTypeHost>>(
      "GET",
      `${BASE_PATH}/public/invitations/types/${typeId}/hosts?${urlParams.toString()}`
    );
  }

  async getSignatures(params: VisitorSignaturesFilter) {
    const urlParams = new URLSearchParams();
    urlParams.set("visitor_email", params.visitor_email);
    for (const id of params.document_ids) {
      urlParams.append("document_ids", id);
    }
    return this.requestWithStatus<VisitorSignature[]>(
      "GET",
      `${BASE_PATH}/public/invitations/signatures?${urlParams.toString()}`
    );
  }
}

// ============================================================================
// Deliveries Client
// ============================================================================

export class DeliveriesClient extends BaseOrganizationEndpoints {
  async list(params?: DeliveriesFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedResult<Delivery>>(
      "GET",
      `${BASE_PATH}/deliveries?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<Delivery>(
      "GET",
      `${BASE_PATH}/deliveries/${id}`
    );
  }

  async create(payload: DeliveryCreatePayload) {
    return this.requestWithStatus<Delivery>(
      "POST",
      `${BASE_PATH}/deliveries`,
      payload
    );
  }

  async update(id: string, payload: DeliveryUpdatePayload) {
    return this.requestWithStatus<Delivery>(
      "PUT",
      `${BASE_PATH}/deliveries/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/deliveries/${id}`
    );
  }

  async generateUploadUrl(payload: GenerateDeliveryUploadURLPayload) {
    return this.requestWithStatus<GenerateImageUploadURLResponse>(
      "POST",
      `${BASE_PATH}/deliveries/uploads`,
      payload
    );
  }

  async scanLabel(payload: ScanDeliveryLabelPayload) {
    return this.requestWithStatus<ScanDeliveryLabelResponse>(
      "POST",
      `${BASE_PATH}/deliveries/scan`,
      payload
    );
  }
}

// ============================================================================
// Visitor Notifications Client
// ============================================================================

export class VisitorNotificationsClient extends BaseOrganizationEndpoints {
  async sendTest(payload: TestNotificationPayload) {
    return this.requestWithStatus<void>(
      "POST",
      `${BASE_PATH}/notifications/test`,
      payload
    );
  }
}
